// ─── Conversation view — the right-hand pane of the chat flyout ───────────────
// See CHAT_E2E_DESIGN.md §4, §5, §8. Renders one thread's message list + composer.
//
// Message content is encrypted/decrypted entirely client-side via
// components/Chat/encryptedMessaging.js (Task #149) and file attachments via
// components/Chat/encryptedAttachments.js (Task #150) — this component never
// sees a plaintext byte (or filename) before it's either been provided by
// this user (about to be encrypted) or successfully decrypted with
// `identity.privateKey` (about to be displayed). When `chatKeyStatus !==
// 'ready'` (chat locked, or the first-run/unlock modal hasn't completed yet —
// see ChatKeyGate.js), the composer stays disabled and every message renders
// as the design doc's explicit fallback for "a device that can't decrypt
// this" — "🔒 Unlock chat to view" — instead of breaking the thread view. A
// message that fails to decrypt even once unlocked (e.g. you joined the
// group after it was sent, so you have no wrapped key for it — expected
// behavior per §4, not a bug) gets its own distinct placeholder rather than
// crashing.

import React, { useEffect, useRef, useState } from 'react';
import { subscribeChatMessages } from '../../firebase/firestore';
import { encryptAndSendMessage, decryptMessage } from './encryptedMessaging';
import { encryptAndSendFile, decryptFileMeta, downloadAndDecryptFile } from './encryptedAttachments';

function formatTime(ts) {
  if (!ts?.seconds) return '';
  const d = new Date(ts.seconds * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ConversationView({ companyId, thread, currentUserId, getUserById, chatKeyStatus, identity }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  // { [messageId]: { kind: 'text', text } | { kind: 'file', name, mimeType, size } | { error: true } }
  const [decrypted, setDecrypted] = useState({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null); // 0–1 while a file is encrypting/uploading
  const [downloadingId, setDownloadingId] = useState(null);
  const bottomRef = useRef(null);
  const decryptedCacheRef = useRef({});
  const fileInputRef = useRef(null);

  const isReady = chatKeyStatus === 'ready' && !!identity?.privateKey;

  useEffect(() => {
    if (!companyId || !thread?.id) { setMessages([]); return; }
    const unsub = subscribeChatMessages(companyId, thread.id, setMessages);
    return unsub;
  }, [companyId, thread?.id]);

  // Switching threads shouldn't show the previous thread's decrypted text
  // for a flash before this thread's messages arrive, so the cache resets
  // alongside the visible state whenever the active thread changes.
  useEffect(() => {
    setDecrypted({});
    decryptedCacheRef.current = {};
  }, [thread?.id]);

  // Decrypts every message currently in view that isn't already in the
  // cache — text bodies via decryptMessage, file attachments via
  // decryptFileMeta (which only unwraps the key + filename, not the file
  // bytes themselves; those are fetched on demand when the user clicks
  // download, see handleDownload). Re-runs whenever the live message list
  // changes (new message arrives) or the identity/lock status changes (e.g.
  // chat just got unlocked, so everything currently on screen should now be
  // retried).
  useEffect(() => {
    if (!isReady) return;
    let cancelled = false;
    (async () => {
      const cache = decryptedCacheRef.current;
      const next = { ...cache };
      let changed = false;
      await Promise.all(messages.map(async (msg) => {
        if (next[msg.id]) return;
        changed = true;
        try {
          if (msg.type === 'file') {
            const { name, mimeType, size } = await decryptFileMeta(msg, identity, currentUserId);
            next[msg.id] = { kind: 'file', name, mimeType, size };
          } else {
            const text = await decryptMessage(msg, identity, currentUserId);
            next[msg.id] = { kind: 'text', text };
          }
        } catch (err) {
          next[msg.id] = { error: true };
        }
      }));
      if (cancelled || !changed) return;
      decryptedCacheRef.current = next;
      setDecrypted(next);
    })();
    return () => { cancelled = true; };
  }, [messages, isReady, identity, currentUserId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending || !isReady || !thread) return;
    setSending(true);
    setSendError('');
    try {
      await encryptAndSendMessage({ companyId, thread, userId: currentUserId, identity, text });
      setDraft('');
    } catch (err) {
      console.error('[ConversationView] send failed', err);
      setSendError(err.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    if (!isReady || uploadProgress !== null) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file || !isReady || !thread) return;
    setSendError('');
    setUploadProgress(0);
    try {
      await encryptAndSendFile({ companyId, thread, userId: currentUserId, identity, file, onProgress: setUploadProgress });
    } catch (err) {
      console.error('[ConversationView] file send failed', err);
      setSendError(err.message || 'Could not send file');
    } finally {
      setUploadProgress(null);
    }
  };

  const handleDownload = async (msg) => {
    if (!isReady || downloadingId) return;
    setDownloadingId(msg.id);
    setSendError('');
    try {
      const { blob, name } = await downloadAndDecryptFile(msg, identity, currentUserId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ConversationView] download failed', err);
      setSendError(err.message || 'Could not download attachment');
    } finally {
      setDownloadingId(null);
    }
  };

  if (!thread) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: 'var(--text-muted)' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        <div style={{ fontSize: 13 }}>Select a chat, or start a new one</div>
      </div>
    );
  }

  const otherMemberNames = (thread.memberIds || [])
    .filter(id => id !== currentUserId)
    .map(id => getUserById(id)?.name || getUserById(id)?.email || 'Unknown')
    .join(', ');
  const headerTitle = thread.type === 'group' ? (thread.name || 'Group chat') : (otherMemberNames || 'Direct message');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Conversation header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {headerTitle}
        </div>
        {thread.type === 'group' && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{(thread.memberIds || []).length} members</div>
        )}
      </div>

      {/* Message list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>
            No messages yet. Say hello!
          </div>
        )}
        {messages.map(msg => {
          const isMine = msg.senderId === currentUserId;
          const entry = decrypted[msg.id];
          const isFileMsg = msg.type === 'file';

          let bodyContent;
          if (!isReady) {
            bodyContent = (<><span aria-hidden="true">🔒</span><span>Unlock chat to view</span></>);
          } else if (!entry) {
            bodyContent = (<><span aria-hidden="true">🔒</span><span>{isFileMsg ? 'Decrypting attachment…' : 'Decrypting…'}</span></>);
          } else if (entry.error) {
            bodyContent = (<><span aria-hidden="true">🔒</span><span>Couldn't decrypt this {isFileMsg ? 'attachment' : 'message'}</span></>);
          } else if (entry.kind === 'file') {
            const isDownloading = downloadingId === msg.id;
            bodyContent = (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true" style={{ fontSize: 16 }}>📎</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{entry.name}</div>
                  <div style={{ fontSize: 10.5, opacity: 0.8 }}>{formatBytes(entry.size)}</div>
                </div>
                <button
                  onClick={() => handleDownload(msg)}
                  disabled={isDownloading}
                  title="Download and decrypt"
                  style={{
                    border: 'none', background: 'rgba(255,255,255,0.2)', borderRadius: 7,
                    width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isDownloading ? 'default' : 'pointer', flexShrink: 0, color: 'inherit',
                  }}
                >
                  {isDownloading ? (
                    <span style={{ fontSize: 10 }}>…</span>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  )}
                </button>
              </div>
            );
          } else {
            bodyContent = <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.text}</span>;
          }

          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', padding: '8px 12px', borderRadius: 14,
                background: isMine ? 'var(--color-primary)' : 'rgba(0,0,0,0.05)',
                color: isMine ? '#fff' : 'var(--text-secondary)',
                fontSize: 13, lineHeight: 1.45, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {bodyContent}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6, alignSelf: 'flex-end' }}>{formatTime(msg.createdAt)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        {uploadProgress !== null && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.round(uploadProgress * 100)}%`, background: 'var(--color-primary)', transition: 'width 0.1s' }} />
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
              Encrypting &amp; uploading attachment… {Math.round(uploadProgress * 100)}%
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />
          <button
            className="btn-ghost"
            disabled={!isReady || uploadProgress !== null}
            onClick={handleAttachClick}
            title={isReady ? 'Send an encrypted attachment' : 'Unlock chat to send attachments'}
            style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, opacity: (!isReady || uploadProgress !== null) ? 0.5 : 1 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </button>
          <textarea
            className="crm-textarea"
            rows={1}
            placeholder={isReady ? 'Type a message…' : 'Unlock chat to send messages'}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleComposerKeyDown}
            disabled={!isReady || sending}
            style={{ flex: 1, resize: 'none', minHeight: 36, maxHeight: 36, opacity: isReady ? 1 : 0.6 }}
          />
          <button
            className="btn-primary"
            disabled={!isReady || sending || !draft.trim()}
            onClick={handleSend}
            title={isReady ? 'Send' : 'Unlock chat to send messages'}
            style={{ width: 36, height: 36, padding: 0, borderRadius: 9, flexShrink: 0, justifyContent: 'center', opacity: (!isReady || sending || !draft.trim()) ? 0.5 : 1 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
        {sendError && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>{sendError}</div>}
        {!isReady && !sendError && (
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 6 }}>
            Unlock your chat to send and read messages.
          </div>
        )}
      </div>
    </div>
  );
}
